import { useCallback, useEffect, useState } from 'react'
import {
  FieldErrors,
  FieldValues,
  UseFormReturn,
  useFormState,
} from 'react-hook-form'
import { MembersFormValuesLike, validateMembersData } from './function'

// ユーザー新規追加・編集フォームの送信ボタンの活性判定。
// members 配列全体を購読すると、1 フィールドの変更ごとに呼び出し元が
// 再レンダリング + 全メンバー検証(O(N)) となり、大規模組織で入力の度にフリーズする。
// そのため「再レンダリングを起こさない」購読(watch コールバック) + デバウンスで、
// 値から直接 valid 判定した結果(真偽値)のみを state に反映する。
// メール形式はここでは formState.errors(onBlur 検証結果)のみを参照する。
// 仮想化でアンマウント中の行のエラーは errors から漏れるため、
// 送信クリック時に呼び出し元が全件を値から一括検証して補完する。
export const SUBMIT_DISABLED_DEBOUNCE_MS = 150

export const useMembersSubmitDisabled = <
  TFieldValues extends MembersFormValuesLike & FieldValues,
>(
  formMethods: UseFormReturn<TFieldValues>
): boolean => {
  const { errors: formErrors } = useFormState({
    control: formMethods.control,
  })
  const hasEmailValidationErrors =
    Array.isArray(formErrors.members) &&
    formErrors.members.some((memberErrors) => Boolean(memberErrors?.email))

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true)
  const recomputeSubmitDisabled = useCallback(
    (errors: FieldErrors<TFieldValues> = formMethods.formState.errors) => {
      const { members } = formMethods.getValues()
      const { isValid } = validateMembersData(members, errors)
      setIsSubmitDisabled((prev) => (prev === !isValid ? prev : !isValid))
    },
    [formMethods]
  )

  useEffect(() => {
    recomputeSubmitDisabled()
    let debounceTimer: ReturnType<typeof setTimeout> | undefined
    const subscription = formMethods.watch(() => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(
        recomputeSubmitDisabled,
        SUBMIT_DISABLED_DEBOUNCE_MS
      )
    })
    return () => {
      subscription.unsubscribe()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [formMethods, recomputeSubmitDisabled])

  // watch コールバックは値変更でのみ発火するため、入力後のデバウンス完了後に
  // onBlur で formErrors だけが変わる場合は、別途活性を再計算する。
  // formErrors は内部だけ更新されることがあるため、エラー有無も依存に含める。
  useEffect(() => {
    recomputeSubmitDisabled(formErrors)
  }, [formErrors, hasEmailValidationErrors, recomputeSubmitDisabled])

  return isSubmitDisabled
}
